import React, { useEffect, useRef } from "react";
import axios from "axios";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "../../misc/Button";
import { Card } from "../../misc/Card";
import CustomToast from "../../misc/CustomToast";
import { cn } from "../../../utils/classMerger";
import { getAuthHeader } from "../../../utils/authHeader";
import { ListingType } from "../../../types/userListing";
import {
    GenerateSlideshowTextRequest,
    GenerateSlideshowTextResponse,
} from "../../../types/slideshow";
import { SlideshowText } from "./types";
import { SlideshowTemplateId } from "./slideshowTemplates";

// One slide = one selected image + its optional overlay text. `previewUrl` is the
// signed image URL; `tag` is the image's content label (used as an AI hint).
export interface SlideshowSlide {
    id: string;
    previewUrl: string;
    tag?: string;
    description?: string;
}

interface SlideshowTextEditorProps {
    slides: SlideshowSlide[];
    texts: SlideshowText[];
    setTexts: (texts: SlideshowText[]) => void;
    // Records the original AI-generated text (pre-edit) for audit on the backend.
    setAiTexts: (texts: SlideshowText[]) => void;
    // MB-1314 (O3): the debug artifact the API recorded for this caption run. Lifted
    // to the parent so it can be sent back on create and linked to the render.
    setCaptionRenderId: (id: string | undefined) => void;
    listingId: string;
    listingType: ListingType;
    // The template chosen in the prior step, so the generated captions stay
    // within the region the template's crop keeps (portrait/flyer).
    template: SlideshowTemplateId;
    // Lifted to the parent so the "Generate Slideshow" button can be disabled
    // while AI slide text is being (re)generated.
    isGeneratingText: boolean;
    setIsGeneratingText: (value: boolean) => void;
}

// Hard caps on each overlay line — must match the server-side truncation in
// listing.service's slideshow-text normalizer (SLIDESHOW_HEADER_MAX_LEN /
// SLIDESHOW_SUBHEADER_MAX_LEN). The subheader allows a fuller phrase; the
// video2 renderer auto-shrinks each line to fit, so these keep it legible.
export const MAX_HEADER_LENGTH = 30;
export const MAX_SUBHEADER_LENGTH = 50;

const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-xl text-sm text-primary placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary";

const CharCount: React.FC<{ value: string; max: number }> = ({ value, max }) => (
    <span
        className={cn(
            "text-xs",
            value.length >= max ? "text-[#fd7400]" : "text-gray-400"
        )}
    >
        {value.length}/{max}
    </span>
);

// Editor for the text shown above each slide in the video. There is one entry
// per selected image (SLIDE 1, SLIDE 2, …) — the count is fixed by the images
// chosen in step 1, so there's no add/remove here. "Regenerate" asks the API to
// draft AI copy for every slide, grounded in the listing's own data.
const SlideshowTextEditor: React.FC<SlideshowTextEditorProps> = ({
    slides,
    texts,
    setTexts,
    setAiTexts,
    setCaptionRenderId,
    listingId,
    listingType,
    template,
    isGeneratingText,
    setIsGeneratingText,
}) => {
    const didAutoGenerate = useRef(false);

    // Keep exactly one text pair per slide, preserving anything already typed.
    useEffect(() => {
        if (texts.length === slides.length) return;
        setTexts(
            slides.map((_, i) => texts[i] ?? { header: "", subHeader: "" })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slides.length]);

    const updateText = (
        index: number,
        key: keyof SlideshowText,
        value: string
    ): void => {
        setTexts(
            texts.map((t, i) => (i === index ? { ...t, [key]: value } : t))
        );
    };

    const regenerate = async (): Promise<void> => {
        if (slides.length === 0) return;
        setIsGeneratingText(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const payload: GenerateSlideshowTextRequest = {
                listingId,
                listingType,
                slideCount: slides.length,
                imageTags: slides.map((s) => s.tag ?? ""),
                imageDescriptions: slides.map((s) => s.description ?? ""),
                // Slide order, so the API can look up each photo's located
                // features and let the model pick a focusPosition (MB-1314).
                imageIds: slides.map((s) => s.id),
                // The chosen template drives the crop-region caption constraint.
                template,
            };
            const { data } = await axios.post<GenerateSlideshowTextResponse>(
                `${backendUrl}/listing/slideshow/generate-text`,
                payload,
                { headers }
            );
            if (Array.isArray(data?.texts) && data.texts.length > 0) {
                const generated = slides.map(
                    (_, i) => data.texts[i] ?? { header: "", subHeader: "" }
                );
                setTexts(generated);
                // Snapshot the untouched AI output so later edits don't lose it.
                setAiTexts(generated);
                // A REGENERATE produces a NEW artifact, so this must overwrite the
                // previous id — otherwise the render would be linked to the captions
                // the user threw away.
                setCaptionRenderId(data.captionRenderId);
            }
        } catch {
            toast.error(
                <CustomToast
                    message="Couldn't generate slide text. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setIsGeneratingText(false);
        }
    };

    // Auto-generate once when the user lands on this step, but only if no text
    // has been entered yet — never overwrite edits or re-fire on navigating back.
    useEffect(() => {
        if (didAutoGenerate.current || slides.length === 0) return;
        const allEmpty = texts.every(
            (t) => !t.header?.trim() && !t.subHeader?.trim()
        );
        if (texts.length === 0 || allEmpty) {
            didAutoGenerate.current = true;
            void regenerate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slides.length]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h4 className="text-base font-semibold text-primary">
                        Slide Text
                    </h4>
                    <p className="text-sm text-gray-500">
                        This text appears above each slide in your video.
                    </p>
                </div>
                <Button
                    id="regenerate-slide-text-btn"
                    type="button"
                    size="sm"
                    onClick={regenerate}
                    disabled={slides.length === 0 || isGeneratingText}
                    className="flex items-center justify-center"
                >
                    {isGeneratingText ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating…
                        </>
                    ) : (
                        <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Regenerate
                        </>
                    )}
                </Button>
            </div>

            {slides.length === 0 && (
                <p className="text-sm text-gray-400">
                    No slides selected — go back and choose images.
                </p>
            )}

            <div className="space-y-3">
                {slides.map((slide, index) => {
                    const text = texts[index] ?? { header: "", subHeader: "" };
                    return (
                        <Card key={slide.id} className="p-3">
                            <div className="flex gap-3">
                                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                                    <img
                                        src={slide.previewUrl}
                                        alt={`Slide ${index + 1} preview`}
                                        className="w-20 h-20 rounded-md object-cover border border-gray-200 bg-gray-100"
                                    />
                                    <span className="text-xs font-bold text-gray-500 tracking-wider">
                                        SLIDE {index + 1}
                                    </span>
                                </div>

                                <div className="flex-1 space-y-2 min-w-0">
                                    <div>
                                        <input
                                            className={inputClass}
                                            placeholder="Header (e.g. 224 THORN STREET)"
                                            maxLength={MAX_HEADER_LENGTH}
                                            value={text.header}
                                            onChange={(e) =>
                                                updateText(
                                                    index,
                                                    "header",
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <div className="flex justify-end mt-1">
                                            <CharCount
                                                value={text.header}
                                                max={MAX_HEADER_LENGTH}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <input
                                            className={inputClass}
                                            placeholder="Subheader (e.g. SEWICKLEY, PA)"
                                            maxLength={MAX_SUBHEADER_LENGTH}
                                            value={text.subHeader}
                                            onChange={(e) =>
                                                updateText(
                                                    index,
                                                    "subHeader",
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <div className="flex justify-end mt-1">
                                            <CharCount
                                                value={text.subHeader}
                                                max={MAX_SUBHEADER_LENGTH}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};

export default SlideshowTextEditor;
